import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Calendar, MapPin, Clock, Users, Heart, Phone, 
  MessageCircle, Share2, Check, Bell, Award, ExternalLink, Filter, Send
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Share } from '@capacitor/share';

const CITIES = [
  "Abidjan", "Bouaké", "Yamoussoukro", "Daloa", "San-Pédro", 
  "Korhogo", "Man", "Gagnoa", "Divo", "Abengourou"
];

const GirlsRoom = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [events, setEvents] = useState([]);
  const [helpLines, setHelpLines] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [registeredEvents, setRegisteredEvents] = useState(new Set());
  const [selectedCity, setSelectedCity] = useState('all');
  const [userCity, setUserCity] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, selectedCity, authLoading]);

  const fetchData = async () => {
    try {
      const cityParam = selectedCity !== 'all' ? `?city=${selectedCity}` : '';
      const [eventsRes, helpRes, statsRes] = await Promise.all([
        api.get(`/girls-room/events${cityParam}`),
        api.get('/girls-room/help-lines'),
        api.get('/girls-room/user-stats')
      ]);
      
      setEvents(eventsRes.data.events || []);
      setHelpLines(helpRes.data.helpLines || []);
      setUserStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (eventId) => {
    try {
      const res = await api.post('/girls-room/participate', { eventId });
      if (res.data.alreadyRegistered) {
        toast({ title: 'Déjà inscrite', description: 'Tu es déjà inscrite à cette session' });
      } else {
        setRegisteredEvents(prev => new Set([...prev, eventId]));
        toast({ 
          title: 'Inscription confirmée!', 
          description: 'Tu recevras un rappel avant la session'
        });
      }
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleConfirmAttendance = async () => {
    try {
      const res = await api.post('/girls-room/confirm-attendance', {});
      if (res.data.badgeEarned) {
        toast({ 
          title: '🌸 Badge débloqué!', 
          description: 'Tu es maintenant Ambassadrice Girls Room!'
        });
      } else {
        toast({ 
          title: 'Participation enregistrée!',
          description: `${res.data.totalAttendance}/3 pour le badge Ambassadrice`
        });
      }
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleShare = async (platform) => {
    const shareText = "Découvre Fleur de Vie, l'app gratuite pour comprendre ton corps et prendre soin de ta santé! 🌸 #FleurDeVie #GirlsRoom";
    const shareUrl = "https://fleurde-vie.com";
    const isNative = Capacitor.isNativePlatform();
    
    // For native share (all platforms at once)
    if (platform === 'native') {
      try {
        await Share.share({
          title: 'Fleur de Vie',
          text: shareText,
          url: shareUrl,
          dialogTitle: 'Partager Fleur de Vie',
        });
        // Record share
        const res = await api.post('/girls-room/share-app', { platform: 'native' });
        if (res.data.badgeEarned) {
          toast({ 
            title: '🌸 Badge débloqué!', 
            description: 'Merci pour tes partages! Tu es Ambassadrice!'
          });
        }
        fetchData();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
        }
      }
      return;
    }
    
    let url = '';
    switch (platform) {
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'telegram':
        url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
        break;
      default:
        break;
    }
    
    if (url) {
      try {
        // Use Browser plugin for native, window.open for web
        if (isNative) {
          await Browser.open({ url });
        } else {
          window.open(url, '_blank');
        }
        
        const res = await api.post('/girls-room/share-app', { platform });
        if (res.data.badgeEarned) {
          toast({ 
            title: '🌸 Badge débloqué!', 
            description: 'Merci pour tes partages! Tu es Ambassadrice!'
          });
        }
        fetchData();
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="text-white p-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold text-white">Ma Girls Room</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-5">
        {/* Hero Section */}
        <Card className="p-6 bg-gradient-to-br from-pink-500/30 to-purple-600/30 border-pink-400/30">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-pink-500/30 flex items-center justify-center">
              <span className="text-4xl">🌸</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Les Girls Rooms
            </h2>
            <p className="text-lg text-pink-200 mb-3">
              Espaces où tu peux tout dire!
            </p>
            <p className="text-pink-100 text-sm leading-relaxed">
              Les Girls Rooms sont des espaces sécurisés dans ton école pour parler librement 
              de tes règles, ta santé, tes questions. L'app Fleur de Vie prolonge ces discussions 
              quand tu es chez toi.
            </p>
          </div>
        </Card>

        {/* City Filter */}
        <Card className="p-4 bg-white/10 border-white/20">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-pink-300" />
            <div className="flex-1">
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Toutes les villes" />
                </SelectTrigger>
                <SelectContent className="bg-purple-800 border-purple-600">
                  <SelectItem value="all">Toutes les villes</SelectItem>
                  {CITIES.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Badge Progress */}
        {userStats && (
          <Card className="p-4 bg-white/10 border-white/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  userStats.hasBadge ? 'bg-pink-500' : 'bg-white/10'
                }`}>
                  <Award className={`w-6 h-6 ${userStats.hasBadge ? 'text-white' : 'text-pink-300'}`} />
                </div>
                <div>
                  <p className="text-white font-semibold">Badge Ambassadrice</p>
                  <p className="text-pink-200 text-sm">
                    {userStats.hasBadge 
                      ? '🎉 Badge obtenu!' 
                      : `${Math.max(userStats.attendances, userStats.shares)}/3 - Participe ou partage!`
                    }
                  </p>
                </div>
              </div>
              {!userStats.hasBadge && (
                <Button
                  onClick={handleConfirmAttendance}
                  size="sm"
                  className="bg-pink-500 hover:bg-pink-600"
                >
                  <Check className="w-4 h-4 mr-1" />
                  J'ai participé
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Upcoming Sessions */}
        <div>
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-pink-300" />
            Prochaines Sessions
          </h3>
          
          {events.length > 0 ? (
            <div className="space-y-3">
              {events.map((event) => (
                <Card 
                  key={event.id}
                  className="p-4 bg-white/10 border-white/20 hover:bg-white/15 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 bg-pink-500/30 rounded text-pink-200 text-xs">
                          {event.city}
                        </span>
                      </div>
                      <h4 className="text-white font-semibold mb-2">{event.theme}</h4>
                      <div className="space-y-1 text-sm text-pink-200">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(event.date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{event.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{event.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>Animée par {event.animator}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleRegister(event.id)}
                      disabled={registeredEvents.has(event.id)}
                      size="sm"
                      className={registeredEvents.has(event.id) 
                        ? 'bg-green-500/50' 
                        : 'bg-pink-500 hover:bg-pink-600'
                      }
                    >
                      {registeredEvents.has(event.id) ? (
                        <>
                          <Check className="w-4 h-4 mr-1" />
                          Inscrite
                        </>
                      ) : (
                        <>
                          <Bell className="w-4 h-4 mr-1" />
                          Me rappeler
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-6 bg-white/5 border-white/10 text-center">
              <Calendar className="w-12 h-12 text-pink-300 mx-auto mb-3 opacity-50" />
              <p className="text-white font-medium mb-2">Bientôt les dates près de chez toi!</p>
              <p className="text-pink-200 text-sm">
                Contacte ton CPE ou ton animatrice Girls Room pour connaître les prochaines sessions.
              </p>
            </Card>
          )}
        </div>

        {/* Share Section */}
        <Card className="p-5 bg-gradient-to-br from-purple-600/30 to-pink-600/30 border-purple-400/30">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-pink-300" />
            Partage avec tes amies
          </h3>
          <p className="text-pink-200 text-sm mb-4">
            Partage Fleur de Vie 3 fois pour obtenir le badge Ambassadrice!
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => handleShare('whatsapp')}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              WhatsApp
            </Button>
            <Button
              onClick={() => handleShare('facebook')}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Facebook
            </Button>
            <Button
              onClick={() => handleShare('twitter')}
              className="flex-1 bg-sky-500 hover:bg-sky-600"
            >
              Twitter
            </Button>
          </div>
          {/* Second row with Telegram and Native Share */}
          <div className="flex gap-3 mt-3">
            <Button
              onClick={() => handleShare('telegram')}
              className="flex-1 bg-blue-500 hover:bg-blue-600"
            >
              <Send className="w-4 h-4 mr-2" />
              Telegram
            </Button>
            <Button
              onClick={() => handleShare('native')}
              className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Plus
            </Button>
          </div>
          {userStats && (
            <p className="text-pink-300 text-xs mt-3 text-center">
              {userStats.shares}/3 partages effectués
            </p>
          )}
        </Card>

        {/* Ministry Credit */}
        <div className="text-center py-4">
          <p className="text-pink-300/70 text-xs">
            Initiative soutenue par le Ministère de l'Éducation Nationale de Côte d'Ivoire
          </p>
        </div>
      </div>

      {/* Floating Help Button */}
      <button
        onClick={() => setShowHelpDialog(true)}
        className="fixed bottom-24 right-4 w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full shadow-lg flex items-center justify-center z-50 hover:scale-110 transition-transform"
        data-testid="need-help-btn"
      >
        <Heart className="w-6 h-6 text-white" />
      </button>

      {/* Help Dialog */}
      <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-center text-xl">
              Besoin d'en parler?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-pink-200 text-sm text-center mb-4">
              Tu n'es pas seule. Voici des ressources pour t'aider.
            </p>

            {/* Option 1: Find Girls Room */}
            <button
              onClick={() => {
                setShowHelpDialog(false);
                // Scroll to events section
              }}
              className="w-full p-4 bg-white/10 rounded-lg border border-white/20 hover:bg-white/20 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-500/30 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-pink-300" />
                </div>
                <div>
                  <p className="text-white font-semibold">Trouver une Girls Room</p>
                  <p className="text-pink-200 text-xs">Voir les sessions près de chez toi</p>
                </div>
              </div>
            </button>

            {/* Option 2: Help Lines */}
            <div className="space-y-2">
              <p className="text-white font-semibold flex items-center gap-2 px-1">
                <Phone className="w-4 h-4 text-pink-300" />
                Lignes d'écoute anonymes
              </p>
              {helpLines.map((line) => (
                <a
                  key={line.number}
                  href={`tel:${line.number.replace(/\s/g, '')}`}
                  className="block p-3 bg-green-500/20 rounded-lg border border-green-400/30 hover:bg-green-500/30 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium text-sm">{line.name}</p>
                      <p className="text-green-200 text-xs">{line.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-300 font-bold">{line.number}</p>
                      <p className="text-green-200 text-xs">{line.hours}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* Option 3: Community */}
            <button
              onClick={() => {
                setShowHelpDialog(false);
                toast({ title: 'Communauté', description: 'Section bientôt disponible!' });
              }}
              className="w-full p-4 bg-white/10 rounded-lg border border-white/20 hover:bg-white/20 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <p className="text-white font-semibold">Poser une question anonyme</p>
                  <p className="text-pink-200 text-xs">Dans la communauté Fleur de Vie</p>
                </div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav active="girlsroom" />
    </div>
  );
};

export default GirlsRoom;
